import FilterIcon from '@/assets/images/FilterIcon.png';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SelectComponent } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { GET_ALL_PROGRAM } from '@/Graphql/queries';
import { clearAllQueryParams, updateQueryParam } from '@/lib/utils';
import { useQuery } from '@apollo/client';
import { Cross1Icon } from '@radix-ui/react-icons';
import dayjs from 'dayjs';
import { ArrowLeft, Plus, Search } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

type ListHeaderProps = {
  userName: string;
  showSearch?: boolean;
  searchPlaceholder?: string;
  onSearchChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  showButton?: boolean;
  buttonLabel?: string;
  onButtonClick?: (program?: string) => void;
  renderButtonIcon?: () => React.ReactNode;
  filterButton?: boolean;
  onFiltersApplied?: (filters: SessionFiltersState) => void;
  filterCount?: number;
  setFilterCount?: React.Dispatch<React.SetStateAction<number>>;
  payment?: boolean;
  paymentDetailsPage?: boolean;
  TransactionDate?: Date | null;
  transactionId?: string;
  sessionCount?: number;
};

export type SessionFiltersState = {
  category: string;
  switchChecked: boolean;
  startDate: Date | null;
  endDate: Date | null;
};

const ListHeader: React.FC<ListHeaderProps> = ({
  userName,
  showSearch = true,
  filterButton = true,
  searchPlaceholder = 'Search by ID',
  onSearchChange,
  showButton = true,
  buttonLabel = 'Schedule New Session',
  onButtonClick,
  renderButtonIcon = () => <Plus className='h-5 w-5 mr-2' />,
  onFiltersApplied,
  filterCount,
  setFilterCount,
  payment,
  paymentDetailsPage,
  TransactionDate,
  transactionId,
  sessionCount,
}) => {
  const [filters, setFilters] = useState<SessionFiltersState>({
    category: '',
    switchChecked: false,
    startDate: null,
    endDate: null,
  });
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState<
    { text: string | undefined; value: string | undefined }[]
  >([]);

  const [greeting, setGreeting] = useState('Good Morning');

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) {
      setGreeting('Good Morning');
    } else if (hour < 18) {
      setGreeting('Good Afternoon');
    } else {
      setGreeting('Good Evening');
    }
  }, []);

  useQuery(GET_ALL_PROGRAM, {
    variables: {
      limit: 100,
      sort: { programName: 1 },
    },
    onCompleted: (data) => {
      if (data && data.getAllProgram) {
        const formattedOptions = data.getAllProgram.map((item) => ({
          text: item?.programName || '',
          value: item?._id,
        }));
        setCategoryOptions(formattedOptions);
      }
    },
  });

  const handleClosePopover = () => {
    setIsPopoverOpen(false);
  };
  const navigate = useNavigate();
  const handleFilterChange = (newFilters: Partial<SessionFiltersState>) => {
    setFilters((prevFilters) => ({ ...prevFilters, ...newFilters }));
  };
  const handleGoBack = () => {
    navigate(-1);
  };
  const handleClearFilters = () => {
    const clearedFilters = {
      category: '',
      switchChecked: false,
      startDate: null,
      endDate: null,
    };
    setFilters(clearedFilters);

    // Clear the query parameters
    clearAllQueryParams();
  };

  const applyFilters = () => {
    updateQueryParam('category', filters.category || '');

    if (filters.startDate) {
      updateQueryParam('startDate', filters.startDate.toISOString());
    } else {
      updateQueryParam('startDate', '');
    }

    if (filters.endDate) {
      updateQueryParam('endDate', filters.endDate.toISOString());
    } else {
      updateQueryParam('endDate', '');
    }
    if (onFiltersApplied) {
      onFiltersApplied(filters);
    }
    const activeFilters = [filters.startDate, filters.endDate, filters.category].filter(
      Boolean,
    ).length;
    setFilterCount && setFilterCount(activeFilters);
    setIsPopoverOpen(false);
  };

  return (
    <div
      className={`flex h-20 py-5 ${paymentDetailsPage ? 'md:px-8' : 'md:px-20'
        } bg-[#040621] items-center justify-center xl:justify-between border-b-2 border-[#212134]`}
    >
      {paymentDetailsPage ? (
        <>
          <div id='userName' className='hidden xl:flex flex-col  text-white'>
            <div className='flex items-center gap-2'>
              <Button
                onClick={handleGoBack}
                id='session-details-back-btn'
                variant='ghost'
                size='icon'
                className='text-white'
              >
                <ArrowLeft className='h-5 w-5' />
              </Button>
              <span className='text-xl font-bold'>
                Payment for {sessionCount ?? 0} Session{(sessionCount ?? 0) > 1 ? 's' : ''}
              </span>
            </div>

            <span className='text-base font-normal pl-12 '>
              {TransactionDate ? dayjs(TransactionDate).format('MMM D') : ''} | #
              {transactionId ?? ''}
            </span>
          </div>
        </>
      ) : payment ? (
        <div id='userName' className='hidden text-xl font-bold xl:flex flex-col gap-2'>
          Payment Summary
          <span className='text-base font-normal'>
            You can see the amount credited to your account for the completed sessions.
          </span>
        </div>
      ) : (
        <div id='userName' className='hidden text-xl font-normal xl:flex flex-row gap-2'>
          {greeting},<span className='text-lg font-bold'>{userName}</span> 👋
        </div>
      )}
      <div id='functionality' className='flex items-center justify-between'>
        {showSearch && (
          <div id='search' className='relative mr-5'>
            <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-white h-5 w-5 font-bold' />
            <Input
              id='searchInput'
              type='text'
              placeholder={searchPlaceholder}
              className='bg-[#212134] border-none rounded-full w-60 md:w-80 h-11 pl-10 placeholder-gray-400 text-sm'
              onChange={onSearchChange}
            />
          </div>
        )}
        <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
          <PopoverTrigger asChild>
            <div id='filterIcon' className='w-11 h-11 cursor-pointer mr-3 relative'>
              {filterButton && <img src={FilterIcon} alt='FilterIcon' className='w-11 h-11' />}
              {filterCount && filterCount > 0 ? (
                <Badge
                  variant={'default'}
                  className='text-black font-semibold text-sm absolute top-[-5px] right-[-5px] w-5 h-5'
                >
                  {filterCount}
                </Badge>
              ) : (
                ''
              )}
            </div>
          </PopoverTrigger>
          <PopoverContent
            side='bottom'
            align='end'
            className='bg-[#151525] text-white rounded shadow-md border-none p-8 mr-5 flex flex-col min-w-[415px] gap-6'
            style={{
              boxShadow: '5px 8px 40.3px 0px #00000040',
              borderTopLeftRadius: '16px',
              borderBottomRightRadius: '16px',
              borderBottomLeftRadius: '16px',
            }}
          >
            <div className='flex flex-row justify-between items-center'>
              <span className='font-bold text-2xl'>Filter</span>
              <Cross1Icon className='cursor-pointer size-4' onClick={handleClosePopover} />
            </div>
            <div>
              <SelectComponent
                id='category'
                values={categoryOptions}
                className='bg-[#212134] border-none rounded-lg h-[61px] text-sm  focus:ring-0 '
                label='Program'
                setValue={(value) => handleFilterChange({ category: value })}
                value={filters.category}
              />
            </div>
            <div className='flex flex-col gap-6'>
              <div className='flex flex-row gap-5'>
                <span className='font-semibold text-base'>Filter by Date</span>
                <Switch
                  checked={filters.switchChecked}
                  onCheckedChange={(checked) => handleFilterChange({ switchChecked: checked })}
                />
              </div>
              <div className='flex flex-col gap-[14px]'>
                <DatePicker
                  placeholder='Start Date'
                  showCalanderIcon
                  selectedDate={filters.startDate || undefined}
                  disabled={!filters.switchChecked}
                  classNames={`${!filters.switchChecked ? 'cursor-default' : ''} `}
                  clearIcon={false}
                  onChange={(date) => handleFilterChange({ startDate: date })}
                  format='MM/DD/YY'
                />
                <DatePicker
                  placeholder='End Date'
                  showCalanderIcon
                  selectedDate={filters.endDate || undefined}
                  disabled={!filters.switchChecked}
                  clearIcon={false}
                  classNames={`${!filters.switchChecked ? 'cursor-default' : ''} `}
                  onChange={(date) => {
                    if (date) {
                      // Set end date to 23:59:59 of the selected day
                      const endOfDay = new Date(date);
                      endOfDay.setHours(23, 59, 59, 999);
                      handleFilterChange({ endDate: endOfDay });
                    } else {
                      handleFilterChange({ endDate: null });
                    }
                  }}
                  format='MM/DD/YY'
                />
              </div>
            </div>
            <Separator className='h-[1px] bg-[#FFFFFF21]' />
            <div className='flex flex-row justify-between items-center'>
              <span
                className='font-normal text-base underline cursor-pointer'
                onClick={handleClearFilters}
              >
                Clear filter
              </span>
              <Button id='Apply-btn' size='lg' onClick={applyFilters}>
                Apply
              </Button>
            </div>
          </PopoverContent>
        </Popover>
        {showButton && (
          <>
            <Button
              size='sm'
              variant='default'
              className='hidden text-black sm:flex items-center justify-center px-5 py-2 w-56 h-auto'
              id='Add-btn'
              onClick={() => onButtonClick?.('fitprime')}
            >
              {renderButtonIcon()}
              {buttonLabel}
            </Button>
            <Button
              variant={'default'}
              size={'icon'}
              id='add-session-sm-btn'
              className='flex items-center justify-center sm:hidden'
              onClick={() => onButtonClick?.('fitprime')}
            >
              <Plus className='w-6 h-6' />
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default ListHeader;
